import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import useSiteConfig from "@/lib/useSiteConfig";

/**
 * Analytics loader
 * ----------------
 * Injects GA4 (gtag) + Meta Pixel (fbq) scripts at runtime when measurement IDs are
 * present in /api/site-config. Admin can rotate IDs without a redeploy.
 *
 * Also auto-fires a page_view/PageView on every route change so GA + Pixel stay in
 * sync with SPA navigation.
 */
export default function Analytics() {
  const cfg = useSiteConfig();
  const loc = useLocation();
  const GA_ID = cfg.ga_measurement_id;
  const PIXEL_ID = cfg.meta_pixel_id;

  // One-time script injection per id (idempotent)
  useEffect(() => {
    if (GA_ID && window.__vyg_ga_id !== GA_ID) {
      window.__vyg_ga_id = GA_ID;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", GA_ID, { send_page_view: false });
    }
    if (PIXEL_ID && window.__vyg_pixel_id !== PIXEL_ID) {
      window.__vyg_pixel_id = PIXEL_ID;
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
      window.fbq("init", PIXEL_ID);
      /* eslint-enable */
    }
  }, [GA_ID, PIXEL_ID]);

  // Per-route page_view / PageView
  useEffect(() => {
    const path = loc.pathname + loc.search;
    if (window.gtag && GA_ID) {
      window.gtag("event", "page_view", { page_path: path, page_location: window.location.href });
    }
    if (window.fbq && PIXEL_ID) {
      window.fbq("track", "PageView");
    }
  }, [loc.pathname, loc.search, GA_ID, PIXEL_ID]);

  return null;
}

/**
 * track() — canonical event bridge.
 *
 * Fires the same logical event to GA4 and Meta Pixel at once, translating names when
 * the two platforms expect different vocabularies (e.g. purchase vs Purchase,
 * begin_checkout vs InitiateCheckout, generate_lead vs Lead).
 *
 * Known event names — use these for consistency:
 *   - "view_content"      (property detail open)            → GA4: view_content · Meta: ViewContent
 *   - "begin_checkout"    (booking page loaded w/ quote)    → GA4: begin_checkout · Meta: InitiateCheckout
 *   - "add_payment_info"  (razorpay order created)          → GA4: add_payment_info · Meta: AddPaymentInfo
 *   - "purchase"          (payment verified)                → GA4: purchase · Meta: Purchase
 *   - "generate_lead"     (contact form submit / WA click)  → GA4: generate_lead · Meta: Lead
 *   - "search"            (stays search submit)             → GA4: search · Meta: Search
 */
const META_EVENT_MAP = {
  view_content: "ViewContent",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  generate_lead: "Lead",
  search: "Search",
};

export const track = (event, params = {}) => {
  if (typeof window === "undefined") return;
  try {
    if (window.gtag) window.gtag("event", event, params);
    if (window.fbq) {
      const metaEvent = META_EVENT_MAP[event] || event;
      const standard = Object.values(META_EVENT_MAP).includes(metaEvent);
      window.fbq(standard ? "track" : "trackCustom", metaEvent, params);
    }
  } catch (err) {
    // Analytics MUST never break the app — log the failure for debugging
    // but swallow the error so the surrounding UI stays functional.
    if (process.env.NODE_ENV !== "production") {
      console.warn("[analytics] track() failed:", event, err);
    }
  }
};
